import { useState } from "react";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

import { submitLungReferral, submitProstateReferral } from "api/Service";
import { useSendReferral } from "features/referrals/hooks/useSendReferral";
import { getServerErrorMessage } from "utils/showMutationError";

import {
  buildCommonPayload,
  buildLungPayload,
  buildProstatePayload,
} from "../utils/buildReferralPayloads";

export const useReferralSubmission = ({
  allFormsData,
  selectedFacility,
  staffMember,
  isSpecialist,
  isLungsReferral,
  isProstateReferral,
  gpDetails,
  onSuccess,
  onSpecialistReferral,
}) => {
  const queryClient = useQueryClient();
  const [isSubmittingLung, setIsSubmittingLung] = useState(false);
  const [isSubmittingProstate, setIsSubmittingProstate] = useState(false);

  const { mutate: sendGenericReferral, isPending: isGenericPending } =
    useSendReferral({
      onSuccess: (data) => {
        if (data?.link) {
          onSpecialistReferral({ link: data.link, referralId: data.referralId });
        } else {
          onSuccess?.();
        }
      },
    });

  const handleSendReferral = async () => {
    const commonPayload = buildCommonPayload({
      allFormsData,
      selectedFacility,
      staffMember,
      isSpecialist,
      isLungsReferral,
      isProstateReferral,
      gpDetails,
    });

    if (isLungsReferral) {
      setIsSubmittingLung(true);
      try {
        const lungPayload = buildLungPayload(commonPayload, allFormsData);

        const tenantVal = localStorage.getItem("kinesin-tenant");
        const response = await submitLungReferral(tenantVal, lungPayload);

        toast.success("Lung Referral Sent Successfully");

        if (response?.link) {
          onSpecialistReferral({
            link: response.link,
            referralId: response.referralId,
          });
        } else {
          onSuccess?.();
        }
      } catch (error) {
        console.error("Lung Referral Error:", error);
        toast.error(
          getServerErrorMessage(error, "Failed to send Lung Referral")
        );
      } finally {
        setIsSubmittingLung(false);
        queryClient.invalidateQueries({ queryKey: ["referralsList"] });
      }
    } else if (isProstateReferral) {
      setIsSubmittingProstate(true);
      try {
        const prostatePayload = buildProstatePayload(
          commonPayload,
          allFormsData
        );

        const tenantVal = localStorage.getItem("kinesin-tenant");
        const response = await submitProstateReferral(
          tenantVal,
          prostatePayload
        );

        toast.success("Prostate Referral Sent Successfully");

        if (response?.link) {
          onSpecialistReferral({
            link: response.link,
            referralId: response.referralId,
          });
        } else {
          onSuccess?.();
        }
      } catch (error) {
        console.error("Prostate Referral Error:", error);
        toast.error(
          getServerErrorMessage(error, "Failed to send Prostate Referral")
        );
      } finally {
        setIsSubmittingProstate(false);
        queryClient.invalidateQueries({ queryKey: ["referralsList"] });
      }
    } else {
      await sendGenericReferral(commonPayload);
    }
    queryClient.invalidateQueries({ queryKey: ["referralsList"] });
  };

  return {
    handleSendReferral,
    isPending: isGenericPending || isSubmittingLung || isSubmittingProstate,
  };
};
